// Theme resolver. The theme is chosen per request from the host (see ../resolveTheme.ts),
// not from the build's VARIANT, so one Flyin build can serve flyin.com and a sub-brand
// like heya.flyin.com with different palettes.

import { ThemeOptions } from '@mui/material/styles'
import { getBrandThemeKey, ThemeKey } from '../resolveTheme'
import cleartripTheme from './cleartrip'
import flyinTheme from './flyin'
import heyaTheme from './heya'

const THEMES: Record<ThemeKey, ThemeOptions> = {
    cleartrip: cleartripTheme,
    flyin: flyinTheme,
    heya: heyaTheme
}

// Shared, brand-agnostic overrides layered on top of whichever theme is selected.
// `themeKey` is stamped onto the theme so components can branch on the active sub-brand
// (useThemeKey/useIsHeya) without re-deriving it from the host.
const withSharedOverrides = (themeKey: ThemeKey, selectedTheme: ThemeOptions): ThemeOptions => ({
    ...selectedTheme,
    themeKey,
    components: {
        ...selectedTheme.components,
        MuiCssBaseline: {
            styleOverrides: {
                '@keyframes ping': {
                    '75%, 100%': {
                        transform: 'scale(2)',
                        opacity: 0
                    }
                }
            }
        },
        MuiTooltip: {
            defaultProps: {
                arrow: true,
                followCursor: true,
                slotProps: {
                    tooltip: {
                        sx: {
                            bgcolor: '#08172C',
                            color: '#fff',
                            p: 1,
                            borderRadius: 1,
                            fontSize: '10px',
                            '& .MuiTooltip-arrow': {
                                color: '#08172C'
                            }
                        }
                    }
                }
            }
        }
    }
})

const cache = {} as Record<ThemeKey, ThemeOptions>

export const getThemeOptions = (themeKey: ThemeKey): ThemeOptions => {
    const key = THEMES[themeKey] ? themeKey : getBrandThemeKey()
    cache[key] = cache[key] || withSharedOverrides(key, THEMES[key])
    return cache[key]
}

// The build's own brand theme. Only for code that runs outside a request (e.g. tests);
// anything rendering for a user should go through getThemeOptions(resolveThemeKey(host)).
export default getThemeOptions(getBrandThemeKey())
